import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, ChevronRight } from 'lucide-react';

// Contact Info Card Component
const ContactInfo = ({ icon: Icon, title, content }) => (
    <div className="flex items-start space-x-4 group">
        <div className="flex-shrink-0">
            <div className="p-3 rounded-xl bg-gray-50 group-hover:bg-purple-50
                          transition-all duration-200">
                <Icon className="h-5 w-5 text-gray-600 group-hover:text-purple-600" />
            </div>
        </div>
        <div>
            <h3 className="text-base font-medium text-gray-900">{title}</h3>
            <div className="text-gray-600 mt-1">{content}</div>
        </div>
    </div>
);

// Input Field Component
const InputField = ({ label, type = 'text', name, value, onChange, required = true, options = [] }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {label}
        </label>
        {type === 'textarea' ? (
            <textarea
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                rows={4}
                className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-200
                         focus:border-purple-400 focus:ring-2 focus:ring-purple-100
                         transition duration-200 outline-none resize-none"
            />
        ) : type === 'select' ? (
            <select
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-200
                         focus:border-purple-400 focus:ring-2 focus:ring-purple-100
                         transition duration-200 outline-none"
            >
                <option value="">Select a subject</option>
                {options.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        ) : (
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-200
                         focus:border-purple-400 focus:ring-2 focus:ring-purple-100
                         transition duration-200 outline-none"
            />
        )}
    </div>
);

// Main Contact Page Component
const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });

    const [submitStatus, setSubmitStatus] = useState({
        submitted: false,
        success: false,
        message: '',
        isSubmitting: false
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus(prev => ({ ...prev, isSubmitting: true }));

        try {
            const response = await fetch('https://formspree.io/f/xbloojqq', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (response.ok) {
                setSubmitStatus({
                    submitted: true,
                    success: true,
                    message: 'Thank you for your message. We will get back to you soon!',
                    isSubmitting: false
                });
                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    message: ''
                });
            } else {
                throw new Error(result.error || 'Failed to submit form');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitStatus({
                submitted: true,
                success: false,
                message: 'Sorry, there was a problem submitting your message. Please try again later.',
                isSubmitting: false
            });
        }
    };

    return (
        <div className="min-h-screen pt-20 bg-white">
            <main className="max-w-5xl mx-auto px-4 py-12">
                {/* Page Header */}
                <div className="text-center mb-12">
                    <h1 className="text-3xl font-semibold text-gray-900">Contact Us</h1>
                    <p className="mt-2 text-gray-600">
                        Get in touch with us for any questions about our ministry or how you can help.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Contact Information */}
                    <div className="space-y-8">
                        {/* Contact Details Card */}
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <h2 className="text-xl font-semibold text-gray-900 mb-6">Get in Touch</h2>
                            <div className="space-y-6">
                                <ContactInfo
                                    icon={Phone}
                                    title="Phone"
                                    content="(268) 773-7316"
                                />
                                <ContactInfo
                                    icon={Mail}
                                    title="Email"
                                    content={
                                        <a href="mailto:communitycharitableministry@gmail.com"
                                           className="text-gray-600 hover:text-purple-600
                                                    transition-colors duration-200">
                                            communitycharitableministry@gmail.com
                                        </a>
                                    }
                                />
                                <ContactInfo
                                    icon={MapPin}
                                    title="Location"
                                    content="Antigua"
                                />
                                <ContactInfo
                                    icon={Clock}
                                    title="Distribution Hours"
                                    content="Last Saturday of every month"
                                />
                            </div>
                        </div>

                        {/* Support Card */}
                        <div className="bg-purple-600 p-6 rounded-xl shadow-sm text-white">
                            <h3 className="text-xl font-semibold mb-3">
                                Looking to Help?
                            </h3>
                            <p className="text-purple-100 mb-6">
                                We're always looking for volunteers and supporters to help us serve
                                our community better.
                            </p>
                            <a href="/support"
                               className="inline-flex items-center px-5 py-2.5 rounded-lg
                                        bg-white text-purple-600 font-medium hover:bg-purple-50
                                        transition-all duration-200 group"
                            >
                                Support Our Ministry
                                <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1
                                                     transition-transform duration-200" />
                            </a>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6">Send Us a Message</h2>
                        {submitStatus.submitted && submitStatus.success ? (
                            <div className="p-6 rounded-xl bg-purple-50 text-purple-600">
                                <p className="text-lg font-medium">{submitStatus.message}</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <InputField
                                    label="Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                />
                                <InputField
                                    label="Email"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                                <InputField
                                    label="Phone (optional)"
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required={false}
                                />
                                <InputField
                                    label="Subject"
                                    type="select"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    options={[
                                        { value: 'general', label: 'General Inquiry' },
                                        { value: 'volunteer', label: 'Volunteer Information' },
                                        { value: 'donation', label: 'Donation Questions' },
                                        { value: 'assistance', label: 'Request Assistance' },
                                        { value: 'other', label: 'Other' }
                                    ]}
                                />
                                <InputField
                                    label="Message"
                                    type="textarea"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                />

                                {submitStatus.submitted && !submitStatus.success && (
                                    <div className="p-4 rounded-lg bg-red-50 text-red-600 text-sm">
                                        {submitStatus.message}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={submitStatus.isSubmitting}
                                    className="w-full flex items-center justify-center gap-2 px-6 py-2.5
                                             mt-6 bg-purple-600 text-white rounded-lg font-medium
                                             hover:bg-purple-700 transition-all duration-200
                                             disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Send className="h-4 w-4" />
                                    <span>
                                        {submitStatus.isSubmitting ? 'Sending...' : 'Send Message'}
                                    </span>
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ContactPage;